import { PAGINATION } from "@config/constant"
import { useAppDispatch } from "@hook/useAppDispatch"
import { useAppSelector } from "@hook/useAppSelector"
import { trans } from "@resources/localization"
import { Row, Table, Pagination, Button, Popover, Modal, message, Card } from "antd"
import { ColumnsType } from "antd/lib/table"
import React, { useEffect, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { AddProduct } from "./AddProduct"
import SecurityService from "@util/SecurityService"
import { PRODUCT_SCOPE, TELESALES_COPE } from "@config/permission"
import { callDeleteProducts, callGetProductsInGroup } from "@reducer/version2/telesales-group-detail.reducer"
import { callGetAllProductGroup } from "@reducer/version2/product.reducer"
import { filter, find } from "lodash"
import { formatDateFull } from "@util/Common"
import { IProductInGroup } from "@domain/version2/TelesalesGroup"

export const Products: React.FC = () => {
    const dispatch = useAppDispatch()
    const { id } = useParams()
    const [open, setOpen] = useState<boolean>(false)
    const total = useAppSelector((state) => state.telesalesGroupDetail.paginationProduct.total)
    const currentPage = useAppSelector((state) => state.telesalesGroupDetail.paginationProduct.currentPage)
    const totalPage = useAppSelector((state) => state.telesalesGroupDetail.paginationProduct.totalPage)
    const pageSize = useAppSelector((state) => state.telesalesGroupDetail.paginationProduct.pageSize)
    const products = useAppSelector((state) => state.telesalesGroupDetail.productsInGroup)
    const loadingList = useAppSelector((state) => state.telesalesGroupDetail.loadingProducts)
    const listGroupProduct = useAppSelector((state) => state.product.listAllProductGroup)
    const [searchParams, setSearchParams] = useSearchParams()
    useEffect(() => {
        dispatch(callGetAllProductGroup())
    }, [dispatch])
    useEffect(() => {
        const params = Object.fromEntries(searchParams.entries())
        dispatch(
            callGetProductsInGroup({
                code: id || "",
                params: {
                    ...params,
                    page: searchParams.has("page") ? Number(searchParams.get("page")) : PAGINATION.DEFAULT_CURRENT_PAGE,
                    pageSize: searchParams.has("pageSize") ? Number(searchParams.get("pageSize")) : PAGINATION.DEFAULT_PAGE_SIZE,
                },
            })
        )
    }, [dispatch, searchParams])
    const confirmDeleteProduct = (record: IProductInGroup) => {
        Modal.confirm({
            title: trans("message.confirm_delete"),
            icon: <ExclamationCircleOutlined />,
            okText: trans("button.delete"),
            cancelText: trans("button.cancel"),
            onOk: () => {
                dispatch(
                    callDeleteProducts({
                        code: id || "",
                        body: {
                            productCodes: [record?.product?.code || ""],
                        },
                    })
                ).then((result: any) => {
                    if (result?.payload?.status === 200) {
                        message.success(trans("message.success"))
                        const params = Object.fromEntries(searchParams.entries())
                        dispatch(
                            callGetProductsInGroup({
                                code: id || "",
                                params: {
                                    ...params,
                                    page: searchParams.has("page") ? Number(searchParams.get("page")) : PAGINATION.DEFAULT_CURRENT_PAGE,
                                    pageSize: searchParams.has("pageSize") ? Number(searchParams.get("pageSize")) : PAGINATION.DEFAULT_PAGE_SIZE,
                                },
                            })
                        )
                    } else {
                        message.error(result?.payload?.response?.data?.message || trans("message.fail"))
                    }
                })
            },
        })
    }
    const columns: ColumnsType<any> = [
        {
            title: trans("product.code"),
            dataIndex: ["product", "code"],
            key: "code",
            width: 120,
            render: (code: string) =>
                SecurityService.can(PRODUCT_SCOPE.PRODUCT_VIEW_ALL) || SecurityService.can(PRODUCT_SCOPE.PRODUCT_VIEW) ? (
                    <Link to={`/product/${code}`}>{code}</Link>
                ) : (
                    code
                ),
        },
        {
            title: trans("product.name"),
            dataIndex: ["product", "name"],
            key: "name",
            width: 150,
        },
        {
            title: trans("product.category"),
            dataIndex: ["product", "productGroupCode"],
            key: "productGroupCode",
            width: 150,
            render: (groupCode: string) => find(listGroupProduct, { code: groupCode })?.name || "",
        },
        {
            title: trans("table.added_by"),
            dataIndex: "createdBy",
            key: "createdBy",
            width: 100,
        },
        {
            title: trans("table.added_at"),
            dataIndex: "createdAt",
            key: "createdAt",
            width: 120,
            render: (date: string) => (date ? formatDateFull(date) : ""),
        },
        {
            title: trans("table.action"),
            dataIndex: SecurityService.can(TELESALES_COPE.GROUP_PRODUCT_UPDATE) ? "action" : "disabledAction",
            key: SecurityService.can(TELESALES_COPE.GROUP_PRODUCT_UPDATE) ? "action" : "disabledAction",
            width: 60,
            render: (action: string, record: IProductInGroup) =>
                SecurityService.can(TELESALES_COPE.GROUP_PRODUCT_UPDATE) && (
                    <Button
                        type="link"
                        icon={<i className="fa-solid fa-trash"></i>}
                        onClick={() => confirmDeleteProduct(record)}
                    />
                ),
        },
    ]
    const handleChangePage = (page: number, pageSize: number) => {
        const oldSearch = Object.fromEntries(searchParams.entries())
        const params: any = {
            ...oldSearch,
            pageSize,
            page: !searchParams.has("pageSize") || Number(oldSearch.pageSize) === pageSize ? page : PAGINATION.DEFAULT_CURRENT_PAGE,
        }
        setSearchParams(params)
    }
    return (
        <Card
            className="space-layout"
            title={total !== 0 ? `${trans("contact.page")} ${currentPage}/${totalPage}(${total})` : ""}
            extra={
                SecurityService.can(TELESALES_COPE.GROUP_PRODUCT_UPDATE) && (
                    <Popover
                        content={
                            <AddProduct
                                setClose={() => {
                                    const params = Object.fromEntries(searchParams.entries())
                                    dispatch(
                                        callGetProductsInGroup({
                                            code: id || "",
                                            params: {
                                                ...params,
                                                page: searchParams.has("page") ? Number(searchParams.get("page")) : PAGINATION.DEFAULT_CURRENT_PAGE,
                                                pageSize: searchParams.has("pageSize") ? Number(searchParams.get("pageSize")) : PAGINATION.DEFAULT_PAGE_SIZE,
                                            },
                                        })
                                    )
                                    setOpen(false)
                                }}
                            />
                        }
                        title={trans("products.page_title")}
                        placement="left"
                        trigger="click"
                        open={open}
                        onOpenChange={() => setOpen((old) => !old)}>
                        <Button type="primary">{trans("products.page_title")}</Button>
                    </Popover>
                )
            }>
            <div className="">
                <Table
                    scroll={{
                        x: true,
                    }}
                    loading={loadingList}
                    columns={filter(columns, (iFil: any) => iFil?.dataIndex !== "disabledAction")}
                    dataSource={products}
                    rowKey={(record) => record.id}
                    pagination={false}
                    locale={{
                        emptyText: trans("table.empty"),
                    }}
                />
            </div>
            <Row justify="end">
                <Pagination
                    className="mg-t-15 text-r"
                    showSizeChanger
                    pageSizeOptions={PAGINATION.DEFAULT_PAGE_SIZE_OPTIONS}
                    defaultCurrent={1}
                    current={currentPage}
                    pageSize={pageSize}
                    total={total}
                    onChange={handleChangePage}
                    locale={{
                        items_per_page: `/${trans("contact.page")}`,
                    }}
                />
            </Row>
        </Card>
    )
}
